import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  BENI_COMPARISON_SETS,
  type ComparisonSetWithoutOwn,
} from 'frontend-utils/utils/constants/benchmark-insight';
import type { BeniMarketCompositionMarket } from 'lumen/models';
import type HotelBenchmarkInsightMarketCompositionRoute from 'lumen/routes/hotel/benchmark-insight/market-composition';
import { BENI_COMPARISON_SET_LONG_TRANSLATIONS } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { ModelFrom } from 'lumen/utils/routes';

export const MARKET_COMPOSITION_TABS: ComparisonSetWithoutOwn[] = [
  BENI_COMPARISON_SETS.MARKET,
  BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS,
  BENI_COMPARISON_SETS.MARKET_CLASS,
  BENI_COMPARISON_SETS.SMART_COMPSET,
  BENI_COMPARISON_SETS.RADIUS,
];
export type MarketCompositionTab = (typeof MARKET_COMPOSITION_TABS)[number];

export const DEFAULT_MARKET_COMPOSITION_TAB = BENI_COMPARISON_SETS.MARKET;

export default class HotelBenchmarkInsightMarketCompositionController extends Controller {
  declare model: ModelFrom<HotelBenchmarkInsightMarketCompositionRoute>;
  queryParams = ['activeTab'];

  @service('benchmark-insight/beni-service')
  declare beniService: Services['benchmark-insight/beni-service'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeTab: MarketCompositionTab = DEFAULT_MARKET_COMPOSITION_TAB;

  get availableTabs(): ComparisonSetWithoutOwn[] {
    if (!this.marketComposition) {
      return [];
    }

    const { allowedComparisonSets } = this.beniService;

    let allTabs =
      this.marketComposition?.markets
        .map((market) => market.comparisonSetType)
        .filter((comparisonSetType) =>
          allowedComparisonSets.includes(comparisonSetType),
        )
        .sort((a, b) => {
          return (
            allowedComparisonSets.indexOf(a) - allowedComparisonSets.indexOf(b)
          );
        }) ?? [];

    const includesBothMarketClasses =
      allTabs.includes(BENI_COMPARISON_SETS.MARKET_CLASS) &&
      allTabs.includes(BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS);
    if (includesBothMarketClasses) {
      allTabs = allTabs.filter(
        (comparisonSetType) =>
          comparisonSetType !== BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS,
      );
    }
    return allTabs;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSmartCompset() {
    return this.activeTab === BENI_COMPARISON_SETS.SMART_COMPSET;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketComposition() {
    if (this.model.marketComposition.last?.value) {
      return this.model.marketComposition.last.value;
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get marketCompositionMarketDict() {
    const dict: Partial<
      Record<ComparisonSetWithoutOwn, BeniMarketCompositionMarket>
    > = {};
    this.marketComposition?.markets.forEach((market) => {
      dict[market.comparisonSetType] = market;
    });
    return dict;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get tabs() {
    return this.availableTabs.map((tabId) => {
      const market = this.marketCompositionMarketDict[tabId];
      const smartCompsetCategories = market?.settings?.convertedWeights ?? [];

      return {
        id: tabId,
        label: this.intl.t(BENI_COMPARISON_SET_LONG_TRANSLATIONS[tabId]),
        market: this.marketCompositionMarketDict[tabId] ?? null,
        isMapVisible: tabId !== BENI_COMPARISON_SETS.SMART_COMPSET,
        smartCompsetCategories,
      };
    });
  }

  @action onCloseModal(): void {
    // Use transitionToRoute instead of the router service's transitionTo.
    // transitionTo causes the benchmark-insight model to reload, which is not desired.
    // We just want to close the modal without having the page load again.
    // See https://github.com/emberjs/ember.js/issues/15801#issuecomment-487780998.
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.benchmark-insight');

    this.usageAnalytics.track('Data Composition Report Closed', {
      eventTrigger: 'modal',
      product: 'benchmark-insight',
    });
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  onInit() {
    if (!this.availableTabs.includes(this.activeTab)) {
      const isMarketClassTab =
        this.activeTab === BENI_COMPARISON_SETS.MARKET_CLASS ||
        this.activeTab === BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS;

      if (isMarketClassTab) {
        const availableMarketClassTab = this.availableTabs.find((tab) => {
          return (
            tab === BENI_COMPARISON_SETS.MARKET_CLASS ||
            tab === BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS
          );
        });
        this.onSelectTab(
          availableMarketClassTab ?? DEFAULT_MARKET_COMPOSITION_TAB,
        );
      }
    }
  }

  @action onSelectTab(tabId: string): void {
    const tab = this.availableTabs.find(
      (marketCompositionTab) => marketCompositionTab === tabId,
    );
    if (tab) {
      this.activeTab = tab;
    }

    this.usageAnalytics.track('Data Composition Report Tab Changed', {
      eventTrigger: 'modal',
      product: 'benchmark-insight',
      tab,
    });
  }
}
